export interface FilterOption {
  key: string;
  value: string;
  meta?: string;
}

export const FILTER_ALL_KEY = 'all';

const toArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  if (typeof value === 'string' && value.length > 0) return [value];
  return [];
};

/**
 * Normalise a multi-select value against the available options.
 *
 * A fully-selected value collapses to `['all']`. An empty value is kept as-is
 * so the explicit None action can clear every checkbox.
 */
export const normaliseSelection = (value: unknown, optionKeys: string[], allKey: string = FILTER_ALL_KEY): string[] => {
  const raw = toArray(value);
  if (raw.includes(allKey)) {
    return [allKey];
  }

  const selected = Array.from(new Set(raw.filter((key) => optionKeys.includes(key))));

  if (optionKeys.length > 0 && selected.length === optionKeys.length) {
    return [allKey];
  }

  return selected;
};

export const isAllSelection = (value: unknown, optionKeys: string[], allKey: string = FILTER_ALL_KEY): boolean => {
  const normalised = normaliseSelection(value, optionKeys, allKey);
  return normalised.length === 1 && normalised[0] === allKey;
};

export const getOptionKeys = (options: FilterOption[], allKey: string = FILTER_ALL_KEY): string[] =>
  options.map((option) => option.key).filter((key) => key !== allKey);

export const getSelectionSummary = (
  value: unknown,
  options: FilterOption[],
  allKey: string = FILTER_ALL_KEY
): string => {
  const optionKeys = getOptionKeys(options, allKey);
  if (optionKeys.length === 0) return 'No options';

  const normalised = normaliseSelection(value, optionKeys, allKey);
  if (normalised.length === 0) {
    return 'None';
  }
  if (normalised.length === 1 && normalised[0] === allKey) {
    return `All (${optionKeys.length})`;
  }

  return `${normalised.length} selected`;
};
